import { initWorkNotificationSound, playWorkNotificationSound } from './workNotificationSound';

// Tab + desktop attention when a Work run finishes or needs the user.
// Web only. No permission prompt — desktop notifications fire only if the
// browser already granted Notification permission (e.g. from push setup).

const TITLE_PREFIX = /^● (Needs you|Done|Paused) · /;

let baseTitle = null;
let current = null;

export function workAttentionCopy(kind, threadTitle) {
  const title = String(threadTitle || 'Work').trim() || 'Work';
  switch (kind) {
    case 'login':
      return { label: 'Needs you', headline: 'Work needs you to sign in', body: title };
    case 'approval':
      return { label: 'Needs you', headline: 'Work needs your approval', body: title };
    case 'question':
      return { label: 'Needs you', headline: 'Work has a question for you', body: title };
    case 'credits':
      return { label: 'Needs you', headline: 'Work needs more credits', body: title };
    case 'done':
    case 'idle':
    case 'completed':
      return { label: 'Done', headline: 'Work finished', body: title };
    case 'budget_exceeded':
      return { label: 'Paused', headline: 'Work reached its run limit', body: title };
    case 'failed':
      return { label: 'Paused', headline: "Work couldn't finish", body: title };
    case 'interrupted':
    case 'stopped':
    case 'paused':
    default:
      return { label: 'Paused', headline: 'Work paused', body: title };
  }
}

export function workAttentionTabTitle(pageTitle, attention) {
  const base = String(pageTitle || 'Gab AI').replace(TITLE_PREFIX, '');
  if (!attention?.label) return base;
  return `● ${attention.label} · ${attention.threadTitle || base}`;
}

// Attention belongs to one thread. Never rewrite a different /c/:id tab —
// that is how "● Done · Investor Pitch Deck" leaked onto the Luna book URL.
/** True only when the current attention prefix belongs on this path. */
export function shouldPreserveAttentionTitle(pathname) {
  return !!(current && shouldApplyWorkAttentionTitle(pathname, current));
}

export function shouldApplyWorkAttentionTitle(pathname, attention) {
  if (!attention?.label) return false;
  const match = String(pathname || '').match(/\/c\/([^/]+)/i);
  if (!match) return false;
  if (!attention.conversationId) return false;
  return String(match[1]) === String(attention.conversationId);
}

function pageTitle() {
  if (typeof document === 'undefined') return 'Gab AI';
  return document.title.replace(TITLE_PREFIX, '') || 'Gab AI';
}

let applyingTitle = false;

function applyTitle(pathname) {
  if (typeof document === 'undefined') return;
  const path = pathname
    || (typeof window !== 'undefined' ? window.location.pathname : '');
  const useAttention = current && shouldApplyWorkAttentionTitle(path, current);
  if (!useAttention) {
    if (TITLE_PREFIX.test(document.title)) {
      applyingTitle = true;
      document.title = baseTitle || pageTitle();
      applyingTitle = false;
    }
    return;
  }
  const next = workAttentionTabTitle(baseTitle || pageTitle(), current);
  if (document.title === next) return;
  applyingTitle = true;
  document.title = next;
  applyingTitle = false;
}

function isViewingConversation(conversationId) {
  if (!conversationId || typeof window === 'undefined') return false;
  const match = window.location.pathname.match(/\/c\/([^/]+)/);
  return !!(match && String(match[1]) === String(conversationId));
}

function maybeDesktopNotify(attention) {
  if (typeof document === 'undefined' || typeof Notification === 'undefined') return;
  if (document.visibilityState === 'visible') return;
  if (Notification.permission !== 'granted') return;
  try {
    const n = new Notification(attention.headline, {
      body: attention.body,
      tag: `work-${attention.taskId || attention.conversationId || attention.kind}`,
      silent: true, // The app owns the sound and its mute preference.
    });
    n.onclick = () => {
      try { window.focus(); } catch { /* ignore */ }
      if (attention.href) window.location.assign(attention.href);
      n.close();
    };
  } catch { /* unsupported or blocked */ }
}

export function signalWorkTabAttention({
  kind,
  threadTitle,
  taskId,
  conversationId,
  playSound = true,
} = {}) {
  if (typeof document === 'undefined') return;
  if (playSound) playWorkNotificationSound(kind);
  if (isViewingConversation(conversationId) && document.visibilityState === 'visible') return;

  const copy = workAttentionCopy(kind, threadTitle);
  current = {
    ...copy,
    kind,
    threadTitle: copy.body,
    taskId: taskId ? String(taskId) : null,
    conversationId: conversationId ? String(conversationId) : null,
    href: conversationId ? `/c/${conversationId}` : null,
  };
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  if (shouldApplyWorkAttentionTitle(path, current)) {
    if (!baseTitle) baseTitle = pageTitle();
    applyTitle(path);
  }
  maybeDesktopNotify(current);
}

export function syncWorkTabAttentionTitle(pathname) {
  if (typeof document === 'undefined') return;
  const path = pathname
    || (typeof window !== 'undefined' ? window.location.pathname : '');
  applyTitle(path);
}

export function clearWorkTabAttention({ taskId, conversationId } = {}) {
  if (!current) return;
  if (taskId && current.taskId && String(taskId) !== current.taskId) return;
  if (conversationId && current.conversationId
    && String(conversationId) !== current.conversationId) return;
  current = null;
  applyTitle();
  baseTitle = null;
}

export function initWorkTabAttention() {
  if (typeof document === 'undefined') return () => {};
  const disposeSound = initWorkNotificationSound();
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    if (current?.conversationId && isViewingConversation(current.conversationId)) {
      clearWorkTabAttention({ conversationId: current.conversationId });
    }
  };
  const onTitleChange = () => {
    if (applyingTitle || !current) return;
    const stripped = pageTitle();
    if (document.title === workAttentionTabTitle(stripped, current)) return;
    baseTitle = stripped;
    applyTitle();
  };
  document.addEventListener('visibilitychange', onVisible);
  const titleEl = document.querySelector('title');
  const observer = titleEl
    ? new MutationObserver(onTitleChange)
    : null;
  observer?.observe(titleEl, { childList: true, characterData: true, subtree: true });
  return () => {
    disposeSound();
    document.removeEventListener('visibilitychange', onVisible);
    observer?.disconnect();
  };
}
